import { CardType } from '../../Prototype/Level/Card/CardType';
import {
    DEFAULT_CARD_HEIGHT,
    DEFAULT_CARD_WIDTH,
    ICardData,
} from '../../Prototype/Level/Card/ICardData';
import { ICardStateData } from '../../State/Card/CardReducer';
import CardsHelper from '../../Utility/CardsHelper';
import { Random } from '../../Utility/Random';

export enum LevelGenerationAlgorithm {
    RANDOM,
    SOLI1,
}

export interface ILevelCardData {
    cardsInPlay: ICardData[];
    cardsInStack: number[];
}

export interface INewCardData {
    cardsInPlay: { [id: string]: ICardStateData };
    cardsInStack: { [id: string]: ICardStateData };
}

export default function LoadCardData(
    levelCardData: ILevelCardData,
    cardsHelper: CardsHelper,
    levelGenerationAlgorithm?: LevelGenerationAlgorithm
): INewCardData {
    // Quick clone.
    levelCardData = {
        cardsInPlay: levelCardData.cardsInPlay.map(c => ({ ...c })),
        cardsInStack: [...levelCardData.cardsInStack],
    };

    const newCardData: INewCardData = {
        cardsInPlay: {},
        cardsInStack: {},
    };

    // To minimize the number of generated decks we must first draw all KNOWN cards specified in the level data.
    // Then all RANDOM cards are drawn. This way no KNOWN values are assigned to a RANDOM card which would force
    // a new deck to be generated.

    // Draw known card values in play.
    levelCardData.cardsInPlay
        .filter(c => c.type === CardType.value && !c.random)
        .forEach(c => cardsHelper.draw(c.value));

    const cardsInStack: ICardData[] = new Array(
        levelCardData.cardsInStack.length
    );

    // Draw known card values in stack.
    levelCardData.cardsInStack.forEach((value: number, index: number) => {
        if (value !== -1) {
            cardsInStack[index] = {
                type: CardType.value,
                random: false,
                value: cardsHelper.draw(value),
            };
        }
    });

    if (
        levelGenerationAlgorithm === undefined ||
        levelGenerationAlgorithm === LevelGenerationAlgorithm.RANDOM
    ) {
        levelGenerationRandom(levelCardData, cardsHelper, cardsInStack);
    } else {
        levelGenerationSOLI1(levelCardData, cardsHelper, cardsInStack);
    }

    // Sort cardData array for proper initial depth-sorting.
    levelCardData.cardsInPlay.sort((a: any, b: any) => {
        return a.depth - b.depth;
    });

    for (const cardData of levelCardData.cardsInPlay) {
        if (cardData.type === CardType.gold && cardData.random) {
            cardData.suit = Random.int(0, 3);
        }
        const card: any = {
            id: cardData.id,
            type: cardData.type,
            random: cardData.random,
            value: cardData.value,
            depth: cardData.depth,
            faceUp: cardData.faceUp,
            sequence: cardData.sequence,
            modifiers: cardData.modifiers,
            x: cardData.x,
            y: cardData.y,
            rotation: (cardData.angle / 180) * Math.PI,
            attempts: 0,
            width: DEFAULT_CARD_WIDTH,
            height: DEFAULT_CARD_HEIGHT,
            suit: cardData.suit,
        };

        newCardData.cardsInPlay[card.id] = card;
    }

    cardsInStack.reverse();
    let stackCardCount = 0;
    for (const cardData of cardsInStack) {
        cardData.id = `stack_card_${++stackCardCount}`;

        const card: any = {
            id: cardData.id,
            type: cardData.type,
            random: cardData.random,
            value: cardData.value,
            depth: 0,
            faceUp: false,
            sequence: null,
            modifiers: [],
            x: -150,
            y: 0,
            rotation: 0,
            attempts: 0,
            width: DEFAULT_CARD_WIDTH,
            height: DEFAULT_CARD_HEIGHT,
        };

        newCardData.cardsInStack[card.id] = card;
    }

    return newCardData;
}

/**
 * Draw random cards for both (in play and stack).
 * @param levelCardData
 * @param cardsHelper
 * @param cardsInStack
 */
function levelGenerationRandom(
    levelCardData: ILevelCardData,
    cardsHelper: CardsHelper,
    cardsInStack: ICardData[]
): void {
    // Draw random cards in play.
    levelCardData.cardsInPlay
        .filter(c => c.type === CardType.value && c.random)
        .forEach(c => c.value = cardsHelper.draw());

    // Draw random cards in stack.
    levelCardData.cardsInStack.forEach((value: number, index: number) => {
        if (value === -1) {
            cardsInStack[index] = {
                type: CardType.value,
                random: true,
                value: cardsHelper.draw(),
            };
        }
    });
}

/**
 * Replicate SOLI 1.0 level gereneration logic.
 * @param levelCardData
 * @param cardsHelper
 * @param cardsInStack
 * @see https://confluence.softgames.de/display/RH/Balancing+Soli+1.0
 */
function levelGenerationSOLI1(
    levelCardData: ILevelCardData,
    cardsHelper: CardsHelper,
    cardsInStack: ICardData[]
): void {
    const randomCardsInPlay: ICardData[] = levelCardData.cardsInPlay.filter(
        c => c.type === CardType.value && c.random
    );
    const randomCardsInStack: number[] = levelCardData.cardsInStack.filter(
        v => v === -1
    );
    const cardValuesInPlay: number[] = [];
    const cardValuesInStack: number[] = [];

    let lastCardValue: number;
    let newCardOnStack: boolean = true;

    // If there is any card defined on stack use this one as lastCardValue.
    // Otherwise get a random one to start the stack.
    const firstRandomCardIndex: number = cardsInStack.findIndex(
        x => x === undefined
    );
    if (firstRandomCardIndex > 0) {
        lastCardValue = cardsInStack[firstRandomCardIndex - 1].value;
    } else {
        lastCardValue = cardsHelper.draw();
        cardValuesInStack.push(lastCardValue);
    }

    while (randomCardsInPlay.length > cardValuesInPlay.length) {
        if (
            Random.value() > 0.35 && // 65% chance to get a random card
            newCardOnStack &&
            randomCardsInStack.length > cardValuesInStack.length
        ) {
            lastCardValue = cardsHelper.draw();
            cardValuesInStack.push(lastCardValue);
            newCardOnStack = false;
        } else {
            lastCardValue = cardsHelper.drawMatch(lastCardValue);
            cardValuesInPlay.push(lastCardValue);
            newCardOnStack = true;
        }
    }

    // Filter by "availables" random cards and set its values (in play).
    while (cardValuesInPlay.length > 0) {
        const availables = availableRandomCardInPlay(randomCardsInPlay);

        let selected: ICardData;
        if (availables.length === 0) {
            // If for some reason there is no more available cards using the old logic, get a random one.
            selected = Random.getRandomItem(randomCardsInPlay);
        } else {
            selected = Random.getRandomItem(availables);
        }
        selected.value = cardValuesInPlay.shift();
    }

    // Fill remaining cards in stack with random values and set stack cards values.
    while (randomCardsInStack.length > cardValuesInStack.length) {
        cardValuesInStack.push(cardsHelper.draw());
    }
    levelCardData.cardsInStack.forEach((value: number, index: number) => {
        if (value === -1) {
            cardsInStack[index] = {
                type: CardType.value,
                random: true,
                value: cardValuesInStack.shift(),
            };
        }
    });
}

/**
 * This logic was adapted from SOLI 1.0.
 * @param cards
 */
function availableRandomCardInPlay(cards: ICardData[]): ICardData[] {
    const halfWidth = DEFAULT_CARD_WIDTH / 2;
    const halfHeight = DEFAULT_CARD_HEIGHT / 2;

    // prettier-ignore
    return cards.filter(card1 => {
        for (const card2 of cards) {
            if (
                (
                    card1.x + halfWidth >= card2.x - halfWidth &&
                    card1.x - halfWidth <= card2.x + halfWidth &&
                    card1.y + halfHeight >= card2.y - halfHeight &&
                    card1.y - halfHeight <= card2.y + halfHeight &&
                    card1.depth < card2.depth &&
                    card2.value === undefined
                ) ||
                    card1.value !== undefined
            ) {
                return false;
            }
        }
        return true;
    });
}
